import { AppDispatch } from '@/main'
import { type RootState } from '@/store'
import {
  Beneficiary,
  ChatMessage,
  Organization,
  OrganizationChatSettings,
  Task,
} from '@/store/beneficiary'
import {
  BeneficiariesGetInitialValuesFromOneResponse,
  ChatSessionsCreateOneResponse,
  ChatSessionsGetInitialValuesFromOneResponse,
  IApi,
} from '@/store/spi/api'
import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { Language } from '@repo/ui/lang'
import { DataStatus, findLastChatSessionId, sleep } from '@repo/ui/utils'

interface ThunkExtra {
  api: IApi
}

type AsyncThunkConfig = {
  extra: ThunkExtra
  dispatch: AppDispatch
  state: RootState
}

const updateChatColors = (colors?: OrganizationChatSettings['colors']) => {
  if (!colors) return
  Object.entries(colors).forEach(([key, value]) => {
    let cssValue = value
    if (Array.isArray(value) && value.every((num) => typeof num === 'number')) {
      cssValue = value.join(',')
    }
    document.documentElement.style.setProperty(key, cssValue as string)
  })
}

export const storeActions = {
  initBeneficiary: createAsyncThunk<
    BeneficiariesGetInitialValuesFromOneResponse,
    {
      beneficiaryId: string
      onInitialised: (data: {
        beneficiary: Beneficiary
        organization: Organization
      }) => void
    },
    AsyncThunkConfig
  >(
    'INIT_BENEFICIARY',
    async (
      { beneficiaryId, onInitialised },
      { extra, fulfillWithValue, rejectWithValue },
    ) => {
      const { api } = extra
      const data = await api.beneficiaries.getInitialValuesFromOne({
        beneficiaryId,
      })
      if (data) {
        updateChatColors(data.organization.chatSettings?.colors)
        onInitialised({
          beneficiary: data.beneficiary,
          organization: data.organization,
        })
        return fulfillWithValue(data)
      }
      return rejectWithValue("Failed to fetch beneficiary's data.")
    },
  ),
  initChatSession: createAsyncThunk<
    ChatSessionsGetInitialValuesFromOneResponse,
    { chatSessionId: string },
    AsyncThunkConfig
  >(
    'INIT_CHAT_SESSION',
    async ({ chatSessionId }, { extra, fulfillWithValue, rejectWithValue }) => {
      const { api } = extra
      const data = await api.chatSessions.getInitialValuesFromOne({
        chatSessionId,
      })
      if (data) return fulfillWithValue(data)
      return rejectWithValue("Failed to fetch chat session's messages.")
    },
  ),
  markTaskAsDone: createAsyncThunk<
    { task: Task },
    { taskId: string },
    AsyncThunkConfig
  >(
    'MARK_TASK_AS_DONE',
    async (
      { taskId },
      { extra, fulfillWithValue, rejectWithValue, getState },
    ) => {
      const { api } = extra
      const beneficiary = getState().beneficiary.beneficiary
      if (DataStatus.isFulfilled(beneficiary)) {
        const beneficiaryId = beneficiary.id
        const data = await api.tasks.markOneAsDone({
          beneficiaryId,
          taskId,
        })
        if (data) return fulfillWithValue(data)
      }
      return rejectWithValue('Failed to mark this task as done.')
    },
  ),
  sendFeedback: createAsyncThunk<
    { created: boolean },
    { feedback: string },
    AsyncThunkConfig
  >(
    'SEND_FEEDBACK',
    async (
      { feedback },
      { extra, fulfillWithValue, rejectWithValue, getState },
    ) => {
      const { api } = extra
      const beneficiary = getState().beneficiary.beneficiary
      const { messageId } = getState().feedback
      if (DataStatus.isFulfilled(beneficiary)) {
        const beneficiaryId = beneficiary.id
        const res = await api.feedback.createOne({
          beneficiaryId,
          messageId,
          feedback,
        })
        if (res) return fulfillWithValue(res)
      }
      return rejectWithValue('Failed to send feedback.')
    },
  ),
  runTaskHelper: createAsyncThunk<void, { message: string }, AsyncThunkConfig>(
    'RUN_TASK_HELPER',
    async ({ message }, { getState, dispatch }) => {
      const chatSessions = getState().beneficiary.chatSessions
      if (DataStatus.isFulfilled(chatSessions)) {
        const chatSessionId = findLastChatSessionId(chatSessions)
        if (!chatSessionId) return
        await dispatch(
          storeActions.initChatSession({
            chatSessionId,
          }),
        ).unwrap()
        dispatch(
          storeActions.sendBeneficiaryMessage({
            message,
          }),
        )
        await sleep(200) // Required
      }
    },
  ),
  updateBeneficiaryLanguage: createAsyncThunk<
    { beneficiary: Beneficiary },
    { language: Language },
    AsyncThunkConfig
  >(
    'UPDATE_BENEFICIARY_LANGUAGE',
    async (
      beneficiaryFields,
      { extra, fulfillWithValue, rejectWithValue, getState },
    ) => {
      const { api } = extra
      const beneficiary = getState().beneficiary.beneficiary
      if (DataStatus.isFulfilled(beneficiary)) {
        const beneficiaryId = beneficiary.id
        const data = await api.beneficiaries.patchOne({
          beneficiaryFields,
          beneficiaryId,
        })
        if (data) {
          return fulfillWithValue(data)
        }
      }
      return rejectWithValue("Failed to patch beneficiary's language.")
    },
  ),
  createChatSession: createAsyncThunk<
    ChatSessionsCreateOneResponse,
    void,
    AsyncThunkConfig
  >(
    'CREATE_CHAT_SESSION',
    async (
      _,
      { extra, fulfillWithValue, rejectWithValue, getState, dispatch },
    ) => {
      const { api } = extra
      const beneficiary = getState().beneficiary.beneficiary
      if (DataStatus.isFulfilled(beneficiary)) {
        const beneficiaryId = beneficiary.id
        const data = await api.chatSessions.createOne({
          beneficiaryId,
        })
        if (data) {
          await dispatch(
            storeActions.initChatSession({
              chatSessionId: data.chatSession.id,
            }),
          ).unwrap()
          return fulfillWithValue(data)
        }
      }
      return rejectWithValue('Failed to create chat session.')
    },
  ),
  openFeedback: createAction<{ messageId: string }>('OPEN_FEEDBACK'),
  closeFeedback: createAction('CLOSE_FEEDBACK'),
  streamMessage: createAction<{ chatMessage: ChatMessage }>('STREAM_MESSAGE'),
  sendBeneficiaryMessage: createAction<{ message: string }>(
    'SEND_BENEFICIARY_MESSAGE',
  ),
}
